import { db, analyticsPromise } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { CONFERENCE_CONFIG } from '../config/conference';

export interface BadgeScanData {
  isBadgeScan: boolean;
  rawParam: string | null;
  rawParamName: string | null;
  timestampMs: number | null;
  formattedTime: string | null;
  scanAgeSeconds: number | null;
  conference: string;
  scanTimeIso: string;
}

/**
 * Parses badge timestamp parameter from URL search query
 * Supports epoch milliseconds (standard Java System.currentTimeMillis())
 * and epoch seconds, across various parameter aliases.
 */
export function parseBadgeScan(): BadgeScanData {
  if (typeof window === 'undefined') {
    return {
      isBadgeScan: false,
      rawParam: null,
      rawParamName: null,
      timestampMs: null,
      formattedTime: null,
      scanAgeSeconds: null,
      conference: CONFERENCE_CONFIG.defaultName,
      scanTimeIso: new Date().toISOString()
    };
  }

  const searchParams = new URLSearchParams(window.location.search);
  let foundParamName: string | null = null;
  let rawValue: string | null = null;

  for (const param of CONFERENCE_CONFIG.supportedTimeParams) {
    if (searchParams.has(param)) {
      foundParamName = param;
      rawValue = searchParams.get(param);
      break;
    }
  }

  const conferenceParam = searchParams.get('conf') || searchParams.get('event') || CONFERENCE_CONFIG.defaultName;

  if (!rawValue) {
    // Check if session previously had an active badge scan in this browsing session
    const saved = getActiveBadgeSession();
    if (saved) {
      return saved;
    }

    return {
      isBadgeScan: false,
      rawParam: null,
      rawParamName: null,
      timestampMs: null,
      formattedTime: null,
      scanAgeSeconds: null,
      conference: conferenceParam,
      scanTimeIso: new Date().toISOString()
    };
  }

  const parsedInt = parseInt(rawValue, 10);
  if (isNaN(parsedInt) || parsedInt <= 0) {
    return {
      isBadgeScan: false,
      rawParam: rawValue,
      rawParamName: foundParamName,
      timestampMs: null,
      formattedTime: null,
      scanAgeSeconds: null,
      conference: conferenceParam,
      scanTimeIso: new Date().toISOString()
    };
  }

  // Normalization: Java epoch milliseconds is 13 digits (e.g. 1725819327000)
  // If 10 digits (epoch seconds), multiply by 1000
  let timestampMs = parsedInt;
  if (timestampMs < 10000000000) {
    timestampMs = timestampMs * 1000;
  }

  const nowMs = Date.now();
  const diffSec = Math.round(Math.abs(nowMs - timestampMs) / 1000);

  let formattedTime = '';
  try {
    const dateObj = new Date(timestampMs);
    formattedTime = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    formattedTime = `${timestampMs}`;
  }

  const result: BadgeScanData = {
    isBadgeScan: true,
    rawParam: rawValue,
    rawParamName: foundParamName,
    timestampMs,
    formattedTime,
    scanAgeSeconds: diffSec,
    conference: conferenceParam,
    scanTimeIso: new Date().toISOString()
  };

  // Cache in sessionStorage so page reloads or subsequent views preserve badge context
  try {
    sessionStorage.setItem('active_badge_session', JSON.stringify(result));
  } catch (e) {
    console.warn('[BadgeTracker] Session storage unavailable', e);
  }

  return result;
}

/**
 * Retrieves the currently active badge scan from sessionStorage if available
 */
export function getActiveBadgeSession(): BadgeScanData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('active_badge_session');
    if (raw) {
      return JSON.parse(raw) as BadgeScanData;
    }
  } catch (e) {
    console.warn('[BadgeTracker] Error reading active badge session', e);
  }
  return null;
}

/**
 * Logs the badge scan to Firestore and Firebase Analytics once per unique scan
 */
export async function logBadgeScan(data: BadgeScanData): Promise<void> {
  if (!data.isBadgeScan || !data.rawParam) return;

  const sessionKey = `logged_badge_scan_${data.rawParamName}_${data.rawParam}`;
  if (typeof window !== 'undefined' && sessionStorage.getItem(sessionKey)) {
    // Already logged in this browser tab
    return;
  }

  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  const payload = {
    rawParam: data.rawParam,
    rawParamName: data.rawParamName,
    badgeTimestampMs: data.timestampMs,
    formattedBadgeTime: data.formattedTime,
    scanAgeSeconds: data.scanAgeSeconds,
    conference: data.conference,
    clientScanTime: data.scanTimeIso,
    userAgent: navigator.userAgent,
    isMobile,
    screen: `${window.innerWidth}x${window.innerHeight}`,
    referrer: document.referrer || 'direct-badge-qr',
    url: window.location.href,
    createdAt: serverTimestamp()
  };

  try {
    // Write scan record to Firestore
    await addDoc(collection(db, 'badge_scans'), payload);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(sessionKey, 'true');
    }
    console.log('[BadgeTracker] Logged scan to Firestore:', payload);
  } catch (err) {
    console.warn('[BadgeTracker] Failed to record badge scan to Firestore:', err);
  }

  // Record to Firebase Analytics if supported
  try {
    const analytics = await analyticsPromise;
    if (analytics) {
      logEvent(analytics, 'badge_qr_scanned', {
        conference: data.conference,
        badge_timestamp: data.timestampMs,
        scan_age_seconds: data.scanAgeSeconds,
        is_mobile: isMobile
      });
    }
  } catch (analyticsErr) {
    console.warn('[BadgeTracker] Analytics logging warning:', analyticsErr);
  }
}
